import React from 'react';
import type { GraphRelation, GraphSnippet, GraphDocument } from '../../services/api';

interface EvidencePanelProps {
  relation:  GraphRelation | null;
  snippets:  GraphSnippet[];
  documents: GraphDocument[];
  onClose:   () => void;
}

const SUPPORT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  strong_direct:   { label: 'Diretto forte', color: 'text-green-400'  },
  indirect:        { label: 'Indiretto',      color: 'text-yellow-400' },
  weak_suggestion: { label: 'Debole',         color: 'text-orange-400' },
  unsupported:     { label: 'Non supportato', color: 'text-red-400'    },
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  upload_document: 'Documento',
  news_api:        'Notizie',
  social_api:      'Social',
  web_scraping:    'Web',
  rss_feed:        'RSS',
};

const LEVEL_COLORS: Record<string, string> = {
  STRONG: 'text-green-400',
  MEDIUM: 'text-yellow-400',
  WEAK:   'text-gray-400',
};

function strengthToLevel(s?: number): 'STRONG' | 'MEDIUM' | 'WEAK' {
  if (s == null || s < 0.4) return 'WEAK';
  if (s >= 0.75) return 'STRONG';
  return 'MEDIUM';
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return null; }
}

export function EvidencePanel({ relation, snippets, documents, onClose }: EvidencePanelProps) {
  if (!relation) return null;

  const supportInfo = relation.support_type ? SUPPORT_TYPE_LABELS[relation.support_type] : null;
  const level       = relation.level ?? strengthToLevel(relation.strength);
  const levelColor  = LEVEL_COLORS[level] ?? 'text-gray-400';
  const flags       = relation.flags ?? {};
  const hasFlags    = flags.ambiguous || flags.needs_review || flags.contradicted;

  const relSnippets = snippets.filter(
    (s) => s.entity_key === relation.subject || s.entity_key === relation.object
  ).slice(0, 6);

  const subjectName = relation.subject.split(':').slice(1).join(':');
  const objectName  = relation.object.split(':').slice(1).join(':');

  return (
    <div className="flex flex-col h-full bg-gray-800 rounded-lg text-white text-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h3 className="font-semibold text-base">Evidenze relazione</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">×</button>
      </div>

      {/* Relation summary */}
      <div className="p-4 border-b border-gray-700">
        <div className="font-semibold text-blue-300 text-sm truncate">{subjectName}</div>
        <div className="flex items-center gap-2 my-1.5">
          <span className="flex-1 h-px bg-gray-600" />
          <span className="text-xs font-semibold text-purple-300 px-2 py-0.5 bg-gray-700 rounded whitespace-nowrap">
            {relation.type}
          </span>
          <span className="flex-1 h-px bg-gray-600" />
        </div>
        <div className="font-semibold text-blue-300 text-sm truncate text-right">{objectName}</div>

        {/* Metrics row */}
        <div className="flex flex-wrap gap-3 text-xs mt-3">
          {relation.strength != null && (
            <div>
              <span className="text-gray-400">Forza: </span>
              <span className="font-mono">{relation.strength.toFixed(2)}</span>
              <span className={`ml-1 font-semibold ${levelColor}`}>({level})</span>
            </div>
          )}
          {relation.validated_confidence != null && (
            <div>
              <span className="text-gray-400">Conf: </span>
              <span className="font-mono">{(relation.validated_confidence * 100).toFixed(0)}%</span>
            </div>
          )}
          {relation.evidence_count != null && (
            <div>
              <span className="text-gray-400">Evidenze: </span>
              <span className="font-mono">{relation.evidence_count}</span>
            </div>
          )}
          {relation.documents_count != null && (
            <div>
              <span className="text-gray-400">Doc: </span>
              <span className="font-mono">{relation.documents_count}</span>
            </div>
          )}
          {supportInfo && (
            <div>
              <span className="text-gray-400">Supporto: </span>
              <span className={supportInfo.color}>{supportInfo.label}</span>
            </div>
          )}
        </div>

        {/* Validator version */}
        {relation.validator_version && (
          <div className="text-xs text-gray-600 mt-1">
            Validato con: <span className="font-mono">{relation.validator_version}</span>
          </div>
        )}

        {/* Flags warnings */}
        {hasFlags && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {flags.contradicted && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/50 border border-red-700 text-red-300">
                Contraddetta
              </span>
            )}
            {flags.ambiguous && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-900/50 border border-yellow-700 text-yellow-300">
                Ambigua
              </span>
            )}
            {flags.needs_review && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-900/50 border border-blue-700 text-blue-300">
                Da rivedere
              </span>
            )}
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">

        {/* Reasoning summary */}
        {relation.reasoning_summary && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Ragionamento validatore
            </h4>
            <p className="text-xs text-gray-300 leading-relaxed bg-gray-750 rounded p-2 border border-gray-700">
              {relation.reasoning_summary}
            </p>
          </div>
        )}

        {/* Snippets */}
        {relSnippets.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Testi di supporto ({relSnippets.length})
            </h4>
            <div className="flex flex-col gap-3">
              {relSnippets.map((s, i) => {
                const srcLabel  = SOURCE_TYPE_LABELS[s.source_type ?? ''] ?? s.source_type ?? '';
                const dateLabel = formatDate(s.published_at);
                return (
                  <div key={i} className="border-l-2 border-purple-500 pl-3">
                    <blockquote className="text-xs text-gray-300 italic leading-relaxed">
                      {s.text.slice(0, 320)}{s.text.length > 320 ? '…' : ''}
                    </blockquote>
                    {(srcLabel || dateLabel) && (
                      <div className="flex gap-2 mt-1 text-xs text-gray-500">
                        {srcLabel && <span>{srcLabel}</span>}
                        {dateLabel && <span>{dateLabel}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Source documents */}
        {documents.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Fonti</h4>
            <div className="flex flex-col gap-1.5">
              {documents.map((d) => (
                <div key={d.id} className="flex items-start gap-2">
                  <span className="text-gray-600 mt-0.5">•</span>
                  <div>
                    <div className="text-xs text-gray-300">{d.title || 'Untitled'}</div>
                    <div className="text-xs text-gray-500">
                      {SOURCE_TYPE_LABELS[d.source_type] ?? d.source_type}
                      {d.published_at && ` · ${formatDate(d.published_at)}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {relSnippets.length === 0 && documents.length === 0 && !relation.reasoning_summary && (
          <p className="text-xs text-gray-500 text-center py-4">Nessuna evidenza disponibile</p>
        )}
      </div>
    </div>
  );
}
